import { Matcher } from '@flakiness/server/common/fql/matcher.js';
import { Stats } from '@flakiness/server/common/stats/stats.js';
import { TestsReport } from '@flakiness/server/common/stats/testsReport.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { Multimap } from '@flakiness/shared/common/multimap.js';
import { ContextType } from '@lit/context';
import { css, html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { api } from '../api.js';
import { CommitRegistry } from '../commitRegistry.js';
import { linkStyles } from '../components/cssstyles.js';
import { DataColumn, DataLoader } from '../components/data-table.js';
import { FKPopupDismissed, FKPopupRequested } from '../components/fk-popup.js';
import { consume, contexts } from '../contexts.js';
import { hrefOrClick, Linkifier } from '../linkifier.js';
import { docsUrl } from '../platformUrl.js';

@customElement('data-table-test-stats-cell')
class DataTableTestCell extends LitElement {
  @property({ attribute: false }) testStats?: WireTypes.TestStats;
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.linkTest, subscribe: true }) private _linkTest?: ContextType<typeof contexts.linkTest>;
  @consume({ context: contexts.linkFQL, subscribe: true }) private _linkFql?: ContextType<typeof contexts.linkFQL>;
  @consume({ context: contexts.fql, subscribe: true }) private _fql?: ContextType<typeof contexts.fql>;

  render() {
    const testStats = this.testStats;
    if (!testStats)
      return nothing;
    const test = testStats.test;
    const suiteElements: TemplateResult[] = [];
    const hl = this._fql?.highlightTest(test);
    const suites = test.titles.slice(0, -1);
    for (let i = 0; i < suites.length; ++i) {
      const title = suites[i];
      if (title.trim().length) {
        const highlight = hl?.get(title);
        suiteElements.push(html`<hl-span .text=${title} .hl=${highlight}></hl-span>`);
      } else {
        suiteElements.push(html`<hl-span class=empty-suite .text=${'∅'}></hl-span>`);
      }
      suiteElements.push(html`<sl-icon name=chevron-right></sl-icon>`)
    }
    suiteElements.pop();

    const annotationTexts = new Multimap<string, string|undefined>();
    for (const annotation of testStats?.annotations ?? [])
      annotationTexts.set(annotation.type, annotation.description);

    const filepathLink = this._fql && test.filePath ? this._linkFql?.(
      this._fql
        .toggleFilter(Matcher.TEST_FILE_CONTAINS.create([test.filePath]))
        .serialize()) : undefined;
    const lineNumberLink = this._fql ? this._linkFql?.(
      this._fql
        .toggleFilter(Matcher.TEST_LN_EQ.create([testStats.lineNumber]))
        .serialize()) : undefined;

    const testName = test.titles.at(-1);
    return html`
      <v-box style="gap: var(--sl-spacing-2x-small);">
        <a class=test-name-link ${hrefOrClick(this._linkTest?.(testStats))}>
          <span class=test-suites>${suiteElements}</span>

          <hl-span class=test-name .text=${testName} .hl=${testName ? hl?.get(testName) : nothing}></hl-span>
        </a>
        <h-box>
          ${testStats.lineNumber > 0 && test.filePath ? html`
            <div class=filepath>
              <a ${hrefOrClick(filepathLink)}>
                <hl-span .text=${test.filePath} .hl=${hl?.get(test.filePath)}></hl-span><!-- use html comment to swallow whitespace
              --></a><a
                ${hrefOrClick(lineNumberLink)}
                ?is-selected=${this._fql?.hasLineFilters() && this._fql?.acceptsLine(testStats.lineNumber)}
              >:${testStats.lineNumber}</a>
            </div>
          ` : nothing}
          <h-box class=artifact-icons>
            ${testStats.hasImage ? html`
              <a ${hrefOrClick(this._linkTest?.(testStats, 'section-images'))}>
                <artifact-icon .type=${"image"}></artifact-icon>
              </a>
            ` : nothing}
            ${testStats.hasVideo ? html`
              <a ${hrefOrClick(this._linkTest?.(testStats, 'section-videos'))}>
                <artifact-icon .type=${"video"}></artifact-icon>
              </a>
            ` : nothing}
            ${testStats.hasTrace ? html`
              <a ${hrefOrClick(this._linkTest?.(testStats, 'section-traces'))}>
                <artifact-icon .type=${"pw-trace"}></artifact-icon>
              </a>
            ` : nothing}
          </h-box>
        </h-box>
        ${testStats.tags?.length || annotationTexts?.size ? html`
          <h-box class=tags-block>
            ${(testStats.tags ?? []).map(tag => html`<test-tag .tag=${tag} .fql=${this._fql}></test-tag>`)}
            ${[...annotationTexts].map(([type, descriptions]) => {
              const description = [...descriptions].filter(d => !!d).join('\n');
              return html`<test-annotation .fql=${this._fql} .annotation=${{
                type,
                description,
                annotationId: `${type}\n${description}` as Stats.AnnotationId,
              } satisfies WireTypes.Annotation}></test-annotation>`;
            })}
          </h-box>
        ` : nothing}
      </v-box>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      overflow: hidden;
    }

    .history-button {
      opacity: 0;
      flex: none;
      margin-left: var(--sl-spacing-2x-small);
      transition: opacity 150ms ease-in;

      &::part(base) {
        padding: 0;
      }
    }

    :host-context(.row:hover) .history-button,
    :host(:focus-within) .history-button,
    :host-context(.row:focus-within) .history-button {
      opacity: 1;
    }

    sl-icon {
      flex: none;
    }

    .filepath {
      word-break: break-all;
      color: var(--sl-color-neutral-500);
    }

    v-box {
      padding: var(--sl-spacing-small) 0;
    }

    .test-name-link {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-2x-small);
      word-break: break-word;

      .test-suites {
        display: inline;
        white-space: wrap;
        font-size: var(--sl-font-size-small);
        font-weight: var(--sl-font-weigth-normal);
        color: var(--sl-color-neutral-500);
      }

      .test-name {
        display: inline;
        white-space: wrap;
        font-size: var(--sl-font-size-normal);
        font-weight: var(--sl-font-weight-semibold);
      }
    }

    .filepath {
      color: var(--sl-color-neutral-500) !important;
      font-size: var(--sl-font-size-small);
      white-space: wrap;

      &[is-selected] {
        background-color: var(--fk-color-highlight);
      }
    }
    
    .artifact-icons {
      font-size: var(--sl-font-size-small);
      color: var(--sl-color-neutral-500);
      flex: none;
    }
    sl-icon { font-size: var(--sl-font-size-2x-small); }

    test-tag {
      font-size: var(--sl-font-size-x-small);
    }

    .tags-block {
      margin-top: var(--sl-spacing-2x-small);
      gap: var(--sl-spacing-x-small);
      flex-wrap: wrap;
    }
  `];
}

export const testStatsColumns = {
  name: {
    sortAxis: 'name',
    width: 'minmax(0, 1fr)',
    sortOrder: ['asc', 'desc'],
    renderHeader() { return html`Test name`; },
    renderElement(stats: WireTypes.TestStats): TemplateResult {
      return html`<data-table-test-stats-cell .testStats=${stats}></data-table-test-stats-cell>`;
    },
  },
  timeline: {
    sortAxis: 'timeline_name',
    sortOrder: ['asc', 'desc'],
    width: 'minmax(0, auto)',
    renderHeader() { return html`Environment`; },
    renderElement(stats: WireTypes.TestStats): TemplateResult {
      return html`<timeline-stats-view style="padding: var(--sl-spacing-small) 0;" .timeline=${stats.timeline}></timeline-stats-view>`;
    },
  },
  duration: {
    sortAxis: 'duration',
    setting: { settingId: 'test-stats.duration', title: 'Duration' },
    renderHeader() { return html`
      <h-box style="gap: var(--sl-spacing-2x-small)">
        <span>Duration</span>
        <fk-help style="font-size: var(--sl-font-size-x-small)">
          <p>The test's duration in the run its status comes from.</p>
          <p>Retries are added up: a test that passed on its third attempt, 10s each, shows 30s.</p>
        </fk-help>
      </h-box>
    `; },
    renderElement({ durationMs }: WireTypes.TestStats): TemplateResult {
      return html`
        <time-interval .ms=${durationMs}></time-interval>
      `;
    },
  },
  flipRate: {
    sortAxis: 'flip_rate',
    setting: { settingId: 'test-stats.flip-rate', title: 'Flip Rate' },
    renderHeader() { return html`
      <h-box style="gap: var(--sl-spacing-2x-small)">
        <span>Flip Rate</span>
        <fk-help href=${docsUrl('/concepts/flip-rate/')} style="font-size: var(--sl-font-size-x-small)">
          <p>How often the test switched between passing and failing across the bars in the History column.</p>
          <p>0% means the result never changed. 100% means it alternated on every bar. A flaky bar counts as a failure followed by a pass.</p>
        </fk-help>
      </h-box>
    `; },
    renderElement({ flipRate }: WireTypes.TestStats): TemplateResult {
      if (flipRate === undefined)
        return html``;
      let pct = flipRate * 100;
      let fmt: string;
      if (Math.abs(pct) < 1e-3)
        fmt = 0..toFixed(0);
      else if (Math.abs(pct - 100) < 1e-3)
        fmt = 100..toFixed(0);
      else
        fmt = pct.toFixed(1);
      return html`${fmt}%`;
    },
  },
  failRate: {
    sortAxis: 'fail_rate',
    setting: { settingId: 'test-stats.fail-rate', title: 'Fail Rate', defaultHidden: true },
    renderHeader() { return html`
      <h-box style="gap: var(--sl-spacing-2x-small)">
        <span>Fail Rate</span>
        <fk-help href=${docsUrl('/concepts/fail-rate/')} style="font-size: var(--sl-font-size-x-small)">
          <p>The share of bars in the History column where the test failed.</p>
          <p>Skipped bars don't count. A flaky bar counts as a pass, because the test passed on retry.</p>
        </fk-help>
      </h-box>
    `; },
    renderElement({ failRate }: WireTypes.TestStats): TemplateResult {
      if (failRate === undefined)
        return html``;
      let pct = failRate * 100;
      let fmt: string;
      if (Math.abs(pct) < 1e-3)
        fmt = 0..toFixed(0);
      else if (Math.abs(pct - 100) < 1e-3)
        fmt = 100..toFixed(0);
      else
        fmt = pct.toFixed(1);
      return html`${fmt}%`;
    },
  },
} satisfies Record<string, DataColumn<WireTypes.TestStats, WireTypes.TestStatsSortAxis>>;

const DEFAULT_SORT_OPTIONS = {
  axis: 'name' as WireTypes.TestStatsSortAxis,
  direction: 'desc' as WireTypes.SortDirection,
}

export function createTestStatsLoaderLocal(statsReport: TestsReport): DataLoader<WireTypes.TestStats, WireTypes.TestStatsSortAxis> {
  return async (pageOptions, sortOptions, signal) => {
    sortOptions ??= DEFAULT_SORT_OPTIONS;
    return statsReport.tests.page(pageOptions, sortOptions.axis, sortOptions.direction);    
  }
}

export function createTestStatsLoaderRemote(commitRegistry: CommitRegistry, options: WireTypes.ReportOptions): DataLoader<WireTypes.TestStats, WireTypes.TestStatsSortAxis> {
  return async (pageOptions, sortOptions, signal) => {
    const result = await api.report.testStats.POST({
      ...options,
      pageOptions: pageOptions,
      sortOptions: sortOptions ?? DEFAULT_SORT_OPTIONS,
    }, { signal });
    commitRegistry.addCommits(result.commits);
    return result.page;
  }
}

export function createStatusColumn(options: {
  commitRegistry: CommitRegistry,
  name?: TemplateResult<1>,
  runLinker?: Linkifier<WireTypes.TestStats>,
}) {
  const statusColumn: DataColumn<WireTypes.TestStats, WireTypes.TestStatsSortAxis> = {
    sortAxis: 'outcome',
    width: 'auto',
    renderHeader() { return options.name ?? html`S`; },
    renderElement(testStats: WireTypes.TestStats): TemplateResult {
      return html`
        <fk-popup distance=10>
          <h-box
            style="justify-content: center; flex: auto;"
            @mouseenter=${(event: MouseEvent) => {
              if (!event.currentTarget || !(event.currentTarget instanceof HTMLElement))
                return;
              const popover = html`
                <v-box style="gap: 0;">
                  <h-box style="padding: var(--sl-spacing-small); border-bottom: 1px solid var(--fk-color-border);">
                    <fk-outcome outcome=${testStats.outcome}></fk-outcome>
                    <span>Run #${testStats.runId}</span>
                  </h-box>
                  <h-box style="padding: var(--sl-spacing-small);">
                    <fk-outcome outcome=${testStats.outcome}></fk-outcome>
                    <fk-commit .commit=${options.commitRegistry?.commit(testStats.commitId)}></fk-commit>
                    <span>•</span>
                    <time-interval .ms=${testStats.durationMs}></time-interval>
                  </h-box>
                </v-box>
              `;
              event.currentTarget?.dispatchEvent(new FKPopupRequested(popover, event.currentTarget));
            }}
            @mouseleave=${(event: MouseEvent) => {
              event.currentTarget?.dispatchEvent(new FKPopupDismissed());
            }}
          >
            <a ${hrefOrClick(options.runLinker?.(testStats))}><fk-outcome .outcome=${testStats.outcome}></fk-outcome></a>
          </h-box>
        </fk-popup>
      `;
    },
  }
  return statusColumn;
}
